import { useState } from 'react'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { serviceAccounts } from '../../api/fee'
import type { ServiceAccount } from '../../api/types'
import { ConfirmButton, Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatDateTime } from '../../lib/format'
import { useMoneyAction } from '../../lib/useMoneyAction'

/**
 * Port of `ServiceAccountListComponent` (`projects/:projectId/service-accounts`,
 * admin only): active service accounts (status 0), description/comment edit,
 * revoke, create, and the legacy "Show login" reveal.
 *
 * The "Show password" reveal was removed (owner decision 2026-10-18, ticket
 * f7604adb): the page never calls `GET …/{id}/pass` (the API answers 410
 * `password_reveal_removed`) and shows a notice instead; service-to-service
 * access moves to fleet m2m. Revoke asks once inline (legacy: first click).
 */
export function ServiceAccountsPage() {
  const { projectId, project } = useProject()
  const q = useApiQuery(() => serviceAccounts.search(projectId), [projectId])
  const actions = useMoneyAction()
  const active = (q.data ?? []).filter((a) => !Number(a.status))

  const create = () => actions.run('create', String(Date.now()), () => serviceAccounts.add(projectId), () => q.refetch())

  return (
    <div className="portal-card">
      <PageTitle title={`Service accounts — ${project.name}`}>
        <button type="button" disabled={actions.isBusy('create')} onClick={() => void create()}>
          {actions.isBusy('create') ? 'Creating…' : 'Create new'}
        </button>
      </PageTitle>
      <Notice feedback={actions.feedbackFor('create')} />
      <p className="portal-notice portal-notice--info" data-testid="password-reveal-removed">
        Service account passwords can no longer be shown. Service-to-service access is moving to fleet m2m.
      </p>
      <QueryState loading={q.loading} error={q.error}>
        {active.length === 0 && <p className="portal-text-muted">No active service accounts.</p>}
        <ul className="fs-plain-list">
          {active.map((a) => (
            <ServiceAccountRow key={a.id} projectId={projectId} account={a} onChanged={() => q.refetch()} />
          ))}
        </ul>
      </QueryState>
    </div>
  )
}

function ServiceAccountRow({
  projectId,
  account,
  onChanged,
}: {
  projectId: string
  account: ServiceAccount
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [description, setDescription] = useState(account.description ?? '')
  const [comment, setComment] = useState(account.comment ?? '')
  const [showLogin, setShowLogin] = useState(false)
  const actions = useMoneyAction()

  const save = async () => {
    const r = await actions.run('save', description + comment, () =>
      serviceAccounts.modify(projectId, account.id, { description, comment }),
    )
    if (r) {
      setEditing(false)
      onChanged()
    }
  }

  return (
    <li className="fs-panel" data-testid={`service-account-${account.id}`}>
      <div className="portal-page-title">
        <div>
          <strong>{account.description || '(no description)'}</strong>
          {!editing && account.comment && <span className="portal-muted-small fs-prewrap">{account.comment}</span>}
          <span className="portal-muted-small">Created at {formatDateTime(account.createdAt)}</span>
        </div>
        <div className="portal-inline-actions">
          <button type="button" onClick={() => setEditing(!editing)} aria-expanded={editing}>
            Edit
          </button>
          <button type="button" onClick={() => setShowLogin(!showLogin)} aria-expanded={showLogin}>
            {showLogin ? 'Hide login' : 'Show login'}
          </button>
          <ConfirmButton
            label="Revoke"
            pendingText="Revoke this service account? Clients using it stop working."
            className="portal-button-danger"
            busy={actions.isBusy('revoke')}
            onConfirm={() => void actions.run('revoke', account.id, () => serviceAccounts.revoke(projectId, account.id), onChanged)}
          />
        </div>
      </div>
      {showLogin && (
        <p className="portal-notice portal-notice--info">
          Login: <code>{account.login}</code>
        </p>
      )}
      {editing && (
        <form
          className="portal-form"
          aria-label="Edit service account"
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <label>
            Description
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label>
            Comment
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
          </label>
          <button type="submit" disabled={actions.isBusy('save')}>
            Save
          </button>
        </form>
      )}
      <Notice feedback={actions.feedbackFor('save') ?? actions.feedbackFor('revoke')} />
    </li>
  )
}
